import { useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import { useQueryClient } from "@tanstack/react-query";

import { useGetCountries } from "@/api/country/useCountryAPI";
import { useGetLanguages } from "@/api/language/useLanguageApi";
import { useTimezonesQuery } from "@/api/timezones/useTimezonesAPI";
import { usePatchUser } from "@/api/user/useUserAPI";
import {
  GET_USER_GROUPS_DEFAULT_PARAMS,
  useGetInfiniteUserGroupsAPI
} from "@/api/userGroups/useUserGroupsAPI";
import { CloseIcon } from "@/assets/icons";
import NotificationContent from "@/components/notification/notificationContent";
import { Button } from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import { IOptionItem } from "@/components/ui/dropdown/types";
import FormField from "@/components/ui/formField";
import { StatusOptions } from "@/components/userManagement/data";
import { MESSAGE } from "@/constants/message";
import { ONLY_NUMERIC_REGEX } from "@/constants/regex";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { resetSidebarUserSettingPopupDisplay } from "@/store/slice/uiSlice";
import { GetUserByIdDetail, PatchUserPayload, UserStatus } from "@/types/user";
import { trimPhoneNumberByCountryCode } from "@/utils/string";

import * as countryCodes from "country-codes-list";

export interface FormValues {
  fullname: string;
  phone_number: string;
  email_address: string;
  country_code: string;
  country: string;
  timezones: string;
  languages: string;
  group_id: string;
  status: Exclude<UserStatus, UserStatus.ALL>;
}

interface EditUserPanelProps {
  userData: GetUserByIdDetail;
  userId: string;
}

export default function EditUserPanel({
  userData,
  userId
}: Readonly<EditUserPanelProps>) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { showConfirmation } = useConfirmationModal();

  const initialValues = useMemo<FormValues>(() => {
    let phoneCountryCode = "+62";
    let phoneLocalNumber = "";

    const pn = userData?.phone_number ?? "";
    if (pn) {
      if (pn.includes("-")) {
        const [cc, num] = pn.split("-");
        // Be robust to inputs like "+62 - 8123" by trimming spaces
        if (cc) phoneCountryCode = cc.trim();
        if (num) phoneLocalNumber = num.replace(/\D/g, "");
      }
    }

    return {
      fullname: userData?.full_name ?? "",
      email_address: userData?.email_address ?? "",
      country_code: phoneCountryCode,
      phone_number: phoneLocalNumber,
      country: userData?.country?.code ?? "",
      timezones: userData?.timezone?.code ?? "",
      languages: userData?.language?.code ?? "",
      group_id: userData?.groups?.[0]?.id ?? "",
      status: userData?.status ?? ""
    };
  }, [userData]);

  const methods = useForm<FormValues>({
    mode: "onBlur",
    values: initialValues,
    resetOptions: { keepDirtyValues: true }
  });

  const {
    watch,
    setValue,
    control,
    register,
    clearErrors,
    resetField,
    handleSubmit,
    formState: { isValid, errors }
  } = methods;

  const countryField = watch("country");
  const countryCode = watch("country_code");

  const { data: countries, isLoading: isGettingCountries } = useGetCountries();
  const { data: languageOptions, isLoading: isGettingLanguages } =
    useGetLanguages();
  const { data: timezones, isLoading: isGettingTimezones } = useTimezonesQuery(
    { country_id: countryField === "global" ? "" : countryField },
    { enabled: !!countryField }
  );

  const {
    data: groupPermissions,
    fetchNextPage: fetchNextGroupPermissions,
    isLoading: isGettingGroupPermissions,
    isFetchingNextPage: isGettingGroupPermissionsNextPage,
    hasNextPage: hasNextGroupPermissionsPage
  } = useGetInfiniteUserGroupsAPI(GET_USER_GROUPS_DEFAULT_PARAMS);

  const { mutate } = usePatchUser({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getInfiniteUserGroups"]
      });
      queryClient.invalidateQueries({
        queryKey: ["getUsers"]
      });

      queryClient.invalidateQueries({
        queryKey: ["getUserById", userId]
      });
      dispatch(resetSidebarUserSettingPopupDisplay());

      showToast({
        type: EToastType.SUCCESS,
        message: (
          <NotificationContent
            title="User Updated"
            description="User has been updated successfully."
          />
        ),
        autoClose: 3000
      });
    },
    onError: (err) => {
      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title="Something Went Wrong"
            description={
              err.message?.message ||
              "Sorry, we're experiencing an issue processing your request. Please try again."
            }
          />
        ),
        autoClose: 3000
      });
    }
  });

  const groupOptions: IOptionItem[] = useMemo(() => {
    return (groupPermissions?.pages.flatMap((page) => page.data) ?? []).map(
      (item) => ({
        label: item.name,
        description: item.description,
        value: item.id
      })
    );
  }, [groupPermissions]);

  const countriesMapping: IOptionItem[] =
    countries
      ?.filter((country) => (country.id?.toLowerCase?.() ?? "") !== "global")
      ?.map((item) => ({
        label: item.name,
        value: item.id
      })) ?? [];

  const timezonesMapping: IOptionItem[] =
    timezones?.map((item) => ({
      label: item.display_name,
      value: item.name
    })) ?? [];

  const statusOptionsMapping: IOptionItem[] = useMemo(
    () =>
      StatusOptions.filter((o) => o.value !== UserStatus.ALL).map((opt) => ({
        label: opt.label,
        value: opt.value,
        disabled: opt.value === UserStatus.NOT_VERIFIED
      })),
    []
  );

  // languageOptions comes from useGetLanguages select with fallback

  const phoneCodeOptions: IOptionItem[] = Array.from(
    new Set(
      countryCodes.all().map((country) => `+${country.countryCallingCode}`)
    )
  ).map((code) => ({
    label: code,
    value: code
  }));

  const onSubmit = (data: FormValues) => {
    const combinedPhone = `${data.country_code}-${data.phone_number}`;

    const originalPhone = userData.phone_number;
    const newPhone = combinedPhone;

    const newEmail = (data.email_address || "").toLowerCase();
    const originalEmail = (userData.email_address || "").toLowerCase();

    const originalCountryId = userData.country?.code ?? "";
    const originalTimezoneId = userData.timezone?.code ?? "";
    const tzId = timezones?.find((item) => item.name === data.timezones)?.id;

    const originalLanguage = userData.language?.code ?? "";
    const originalGroupId = userData.groups?.[0]?.id ?? "";
    const groupMemberId = userData.groups?.[0]?.group_member_id;

    const payload: Partial<PatchUserPayload> = {
      ...((data.fullname || "") !== (userData.full_name || "")
        ? { fullname: data.fullname }
        : {}),
      ...(newEmail !== originalEmail ? { email_address: newEmail } : {}),
      ...(newPhone !== originalPhone ? { phone_number: combinedPhone } : {}),
      ...((data.country || "") !== originalCountryId
        ? { country_id: data.country }
        : {}),
      ...((data.timezones || "") !== originalTimezoneId
        ? { timezone_id: tzId }
        : {}),
      ...((data.languages || "") !== originalLanguage
        ? { language_code: data.languages }
        : {}),
      ...(data.status !== userData.status ? { status: data.status } : {}),
      ...((data.group_id || "") !== originalGroupId
        ? { group_id: data.group_id, group_member_id: groupMemberId }
        : {})
    };

    // Optionally drop undefined values if your API dislikes them:
    // Object.keys(payload).forEach((k) => payload[k as keyof typeof payload] === undefined && delete payload[k as keyof typeof payload]);
    if (Object.keys(payload).length > 0) {
      mutate({ userId, userData: payload });
    }
  };

  const handleClosePanel = () => {
    dispatch(resetSidebarUserSettingPopupDisplay());
  };

  const handleCloseConfirmation = () => {
    showConfirmation({
      title: MESSAGE.SETTING_SAVE_USER_TITLE_CLOSE,
      message: (
        <div className="text-center">
          {MESSAGE.SETTING_SAVE_USER_DESCRIPTION_CLOSE}
        </div>
      ),
      cancelLabel: "Cancel",
      confirmLabel: "Leave",
      onConfirm: () => handleClosePanel()
    });
  };

  return (
    <div
      className="
        absolute top-4 right-4 z-[9] border-gainsboro bg-white rounded-lg w-[456px] h-[calc(100vh-32px)] overflow-y-auto
        flex flex-col"
    >
      <div className="sticky top-0 bg-white z-10 px-[24px] py-3 shadow-sm">
        <div className="flex flex-row items-center justify-between">
          <h2 className="text-txtHeading font-sans font-bold text-[20px]">
            Edit User
          </h2>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleCloseConfirmation()}
              className="p-0 bg-transparent border-0"
              aria-label="Close Edit User Panel"
            >
              <CloseIcon className="fill-dim-gray" />
            </button>
          </div>
        </div>
      </div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} aria-label="Edit User">
          <div className="mt-3 space-y-5">
            <div className="flex flex-col px-[24px]">
              <span className="font-sans text-sm font-semibold text-txtHeading">
                Personal Information
              </span>
              <div className="mt-3 space-y-3">
                <FormField<FormValues>
                  name="fullname"
                  placeHolder="Full name"
                  register={register}
                  type="text"
                  onClearErrors={(field) => {
                    clearErrors(field as keyof FormValues);
                  }}
                  rules={{
                    required: true
                  }}
                  autoCleanError={false}
                  resetValue={() => {
                    resetField("fullname");
                  }}
                  error={errors.fullname?.message}
                />
                <FormField<FormValues>
                  name="email_address"
                  register={register}
                  placeHolder="Email address"
                  type="text"
                  onClearErrors={(field) => {
                    clearErrors(field as keyof FormValues);
                  }}
                  rules={{
                    required: true
                  }}
                  autoCleanError={true}
                  resetValue={() => {
                    resetField("email_address");
                  }}
                  error={errors.email_address?.message}
                />
                <div className="flex flex-row items-center gap-3">
                  <Controller
                    name="country_code"
                    control={control}
                    defaultValue="+62"
                    rules={{ required: "Country Code is required" }}
                    render={({ field: { onChange, value, onBlur } }) => (
                      <Dropdown
                        className="text-sm w-[135px]"
                        value={
                          phoneCodeOptions?.find((c) => c.value === value) ||
                          null
                        }
                        aria-label="Country Code"
                        data={phoneCodeOptions}
                        placeholder="Country Code"
                        labelBorder=""
                        onSelect={(val) => {
                          onChange(val?.value ?? "");
                          onBlur();
                        }}
                        withSearch
                        filterMatchedSearch
                      />
                    )}
                  />
                  <FormField<FormValues>
                    name="phone_number"
                    register={register}
                    placeHolder="Phone number"
                    autoCleanError={false}
                    type="tel"
                    rules={{
                      required: true
                    }}
                    onBlur={(e) => {
                      // We need to trim the phone number based on the selected country code
                      const regex = trimPhoneNumberByCountryCode(
                        countryCode || "+62"
                      );
                      const value = e.target.value.replace(regex, "");
                      setValue("phone_number", value);
                    }}
                    onChange={(e) => {
                      const value = e.target.value.replace(
                        ONLY_NUMERIC_REGEX,
                        ""
                      );
                      setValue("phone_number", value, {
                        shouldValidate: true
                      });
                    }}
                    onClearErrors={(field) => {
                      clearErrors(field as keyof FormValues);
                    }}
                    resetValue={() => {
                      resetField("phone_number");
                    }}
                    error={errors.phone_number?.message}
                  />
                </div>
                <div className="flex flex-col gap-1 mt-3">
                  <span className="px-2 text-[10px] text-neutral-600">
                    Status
                  </span>
                  <Controller
                    name="status"
                    control={methods.control}
                    render={({ field: { onChange, value, onBlur } }) => (
                      <Dropdown
                        value={
                          statusOptionsMapping.find((c) => c.value === value) ||
                          null
                        }
                        data={statusOptionsMapping}
                        placeholder="Select status"
                        // Avoid floating label since we already render a label above
                        showLabelOnTop={false}
                        onSelect={(val) => {
                          onChange(val?.value as FormValues["status"]);
                          onBlur();
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="flex space-y-4 flex-col px-[24px]">
              <span className="font-sans text-sm font-semibold text-txtHeading">
                Country and Languages
              </span>
              <Controller
                name="country"
                control={control}
                rules={{ required: "Country is required" }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <Dropdown
                    name="country"
                    value={
                      countriesMapping.find((c) => c.value === value) || null
                    }
                    data={countriesMapping}
                    placeholder="Country"
                    labelBorder="Country"
                    aria-label="Country"
                    onSelect={(val) => {
                      onChange(val?.value ?? "");
                      setValue("timezones", "");
                      onBlur();
                    }}
                    isLoading={isGettingCountries}
                  />
                )}
              />
              <Controller
                name="timezones"
                control={control}
                rules={{ required: "Timezone is required" }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <Dropdown
                    name="timezones"
                    value={
                      timezonesMapping.find((c) => c.value === value) || null
                    }
                    data={timezonesMapping}
                    placeholder="Timezone"
                    labelBorder="Timezone"
                    onSelect={(val) => {
                      onChange(val?.value ?? "");
                      onBlur();
                    }}
                    isLoading={isGettingTimezones}
                  />
                )}
              />

              <Controller
                name="languages"
                control={control}
                rules={{ required: "Language is required" }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <Dropdown
                    value={
                      languageOptions?.find((c) => c.value === value) || null
                    }
                    data={languageOptions ?? []}
                    placeholder="Preferred language"
                    labelBorder="Preferred language"
                    isLoading={isGettingLanguages}
                    onSelect={(val) => {
                      onChange(val?.value ?? "");
                      onBlur();
                    }}
                  />
                )}
              />
            </div>
            <div className="flex space-y-4 flex-col px-[24px]">
              <span className="font-sans text-sm font-semibold text-txtHeading">
                Group
              </span>
              <Controller
                name="group_id"
                control={control}
                rules={{ required: "Group is required" }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <Dropdown
                    value={groupOptions.find((c) => c.value === value) || null}
                    data={groupOptions}
                    placeholder="Select group permission"
                    labelBorder="Group"
                    className="h-[100px]"
                    isLoading={isGettingGroupPermissions}
                    onSelect={(val) => {
                      onChange(val?.value ?? "");
                      onBlur();
                    }}
                    hasMore={hasNextGroupPermissionsPage}
                    isLoadingMore={isGettingGroupPermissionsNextPage}
                    onLoadMore={fetchNextGroupPermissions}
                    withSearch
                    filterMatchedSearch
                  />
                )}
              />
            </div>
          </div>
          <div className="flex px-4 justify-center gap-2 absolute bottom-0 bg-white w-full py-[20px] rounded-t-none rounded-lg">
            <Button
              variant="outline"
              className="h-[40px] w-[198px] btn-secondary text-sm font-bold"
              onClick={handleCloseConfirmation}
              type="button"
            >
              <span>Cancel</span>
            </Button>
            <Button
              className="h-[40px] w-[198px] btn-primary text-sm font-bold"
              disabled={!isValid}
              type="submit"
            >
              <span>Update</span>
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
